import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Norvig brand mark — single source of truth.
 *
 * Two photographic logo families (both share monogram + serif NORVIG TALENTS):
 *   • gold (recolored from copper master via Pillow luminance-remap, WCAG AAA
 *     8.94:1 average contrast against INK) — for DARK backgrounds.
 *   • ink (flat #0A0E1B re-tint) — for LIGHT backgrounds.
 *
 * Variants are EXPLICIT — no auto-detection. Caller knows the background.
 *
 *   wordmark-text  → Pure serif "NORVIG" in currentColor.
 *                    Lightweight text fallback; useful when you only want type.
 *
 *   wordmark-ink   → INK horizontal lockup (NG + NORVIG + TALENTS).
 *                    Default for light backgrounds (navbars, headers).
 *
 *   stacked-ink    → INK monogram-on-top vertical lockup for light backgrounds.
 *
 *   monogram-ink   → INK NG-circle only, square.
 *
 *   wordmark-gold  → GOLD horizontal lockup. For DARK backgrounds.
 *
 *   stacked-gold   → GOLD stacked. For DARK footers / splash.
 *
 *   monogram-gold  → GOLD NG-circle only. For DARK badges.
 */

// Aspect ratios from actual PNG dimensions in /public/brand/
const IMG_ASSETS = {
  // Ink (for light backgrounds — bone/white)
  'wordmark-ink':  { src: '/brand/norvig-wordmark-ink-2026.png',     ar: 1200 / 251 },
  'monogram-ink':  { src: '/brand/norvig-monogram-ink-2026.png',     ar: 1 },
  'stacked-ink':   { src: '/brand/norvig-logo-stacked-ink-2026.png', ar: 680 / 611 },
  // Gold (for dark backgrounds — INK #0A0E1B. WCAG AAA: 8.94:1 average contrast)
  'wordmark-gold': { src: '/brand/norvig-wordmark-gold-2026.png',    ar: 1200 / 251 },
  'wordmark-bronze': { src: '/brand/norvig-wordmark-bronze-2026.png', ar: 1200 / 251 },
  'monogram-gold': { src: '/brand/norvig-monogram-gold-2026.png',    ar: 1 },
  'stacked-gold':  { src: '/brand/norvig-logo-stacked-gold-2026.png',ar: 680 / 611 },
};

const Logo = ({
  variant = 'wordmark-ink',
  size = 28,
  asLink = false,
  alt = 'Norvig',
  color = 'currentColor',
  className = '',
  ...rest
}) => {
  let content;

  if (variant === 'wordmark-text') {
    content = (
      <span
        className={`font-display select-none leading-none tracking-[0.06em] ${className}`}
        style={{
          fontWeight: 600,
          fontOpticalSizing: 'auto',
          fontSize: size,
          color,
          lineHeight: 1,
          display: 'inline-block',
        }}
        aria-label={alt}
        {...rest}
      >
        NORVIG
      </span>
    );
  } else {
    const cfg = IMG_ASSETS[variant] || IMG_ASSETS['wordmark-ink'];
    content = (
      <img
        src={cfg.src}
        alt={alt}
        width={Math.round(size * cfg.ar)}
        height={size}
        draggable={false}
        className={`select-none object-contain ${className}`}
        style={{ height: size, width: 'auto' }}
        {...rest}
      />
    );
  }

  if (asLink) {
    return (
      <Link to="/" aria-label="Norvig hjem" className="inline-flex items-center">
        {content}
      </Link>
    );
  }
  return content;
};

export default Logo;
